import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Star, MessageSquare, Loader2, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { api } from '../lib/api';
import StarRating from '../components/StarRating';

const barColors = ['#dc2626', '#f97316', '#eab308', '#84cc16', '#10b981'];

export default function TeacherFeedbackMine() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [filterRating, setFilterRating] = useState(0); // 0 = all

  const fetchMine = async () => {
    setLoading(true);
    try {
      const res = await api.getMyFeedback();
      setSummary(res.data.summary);
      setFeedbacks(res.data.feedbacks || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load your feedback');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchMine(); }, []);

  const chartData = useMemo(() => {
    if (!summary) return [];
    return [1, 2, 3, 4, 5].map((n) => ({
      rating: `${n}★`,
      count: summary.distribution?.[String(n)] || 0,
    }));
  }, [summary]);

  const filtered = useMemo(() => {
    if (!filterRating) return feedbacks;
    return feedbacks.filter((f) => f.rating === filterRating);
  }, [feedbacks, filterRating]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-slate-400" size={32} /></div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 px-2 sm:px-0" data-testid="teacher-feedback-mine">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-headings text-slate-900">My Feedback</h1>
        <p className="text-sm text-slate-600 mt-1">Feedback students have submitted about your teaching.</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-xl p-4 bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-lg">
          <div className="text-xs uppercase tracking-wider opacity-80">Average Rating</div>
          <div className="text-4xl font-bold mt-1 flex items-center gap-2">
            {summary?.avg_rating ?? 0}
            <Star fill="white" className="text-white opacity-90" size={26} />
          </div>
          <div className="mt-2"><StarRating value={Math.round(summary?.avg_rating || 0)} readonly size={18} testIdPrefix="mine-avg" /></div>
        </div>
        <div className="rounded-xl p-4 bg-gradient-to-br from-indigo-500 to-indigo-700 text-white shadow-lg">
          <div className="text-xs uppercase tracking-wider opacity-80">Total Ratings</div>
          <div className="text-4xl font-bold mt-1">{summary?.total_ratings || 0}</div>
        </div>
        <div className="rounded-xl p-4 bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg">
          <div className="text-xs uppercase tracking-wider opacity-80">5-star Feedback</div>
          <div className="text-4xl font-bold mt-1">{summary?.distribution?.['5'] || 0}</div>
          <div className="text-xs mt-1 opacity-80 flex items-center gap-1"><TrendingUp size={14} /> Great work!</div>
        </div>
      </div>

      {/* Distribution + filter chips */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4">
          <div className="font-semibold text-slate-800 mb-3">Rating Distribution</div>
          {(summary?.total_ratings || 0) === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">No ratings yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="rating" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {chartData.map((_, idx) => <Cell key={idx} fill={barColors[idx]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="font-semibold text-slate-800 mb-3">Filter comments</div>
          <div className="flex flex-wrap gap-2">
            {[0, 5, 4, 3, 2, 1].map((r) => (
              <button key={r} onClick={() => setFilterRating(r)}
                      data-testid={`mine-filter-${r}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                        filterRating === r
                          ? 'bg-amber-600 text-white border-amber-600'
                          : 'bg-white text-slate-700 border-slate-200 hover:border-amber-400'
                      }`}>
                {r === 0 ? 'All' : `${r} ★`}
              </button>
            ))}
          </div>
          <div className="mt-4 text-xs text-slate-500">Showing {filtered.length} feedback{filtered.length !== 1 ? 's' : ''}.</div>
        </div>
      </div>

      {/* Feedback list */}
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {filtered.length === 0 && (
          <div className="p-12 text-center text-sm text-slate-500">
            <MessageSquare size={36} className="mx-auto mb-2 opacity-40" />
            No feedback matches your filters.
          </div>
        )}
        {filtered.map((f) => (
          <div key={f.id + f.rating} className="p-4" data-testid={`mine-feedback-${f.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {f.student_name} <span className="text-slate-400 font-normal">· {f.student_phone_masked}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {f.course_name} · {new Date(f.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                </div>
                {f.comment && (
                  <div className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words bg-slate-50 rounded-lg p-3">
                    "{f.comment}"
                  </div>
                )}
              </div>
              <div className="shrink-0 flex flex-col items-end gap-1">
                <StarRating value={f.rating} readonly size={16} testIdPrefix={`mine-star-${f.id}`} />
                <span className="text-xs font-bold text-amber-700">{f.rating}/5</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
